import { z } from "zod";
import { nookSummarySchema } from "./nook.js";
/**
 * Ask (block 17) — an agent over the caller's own library, streamed as SSE.
 * Threads persist and take follow-ups; block 42 lists and deletes them.
 */
/** WhatsApp's own message limit, so a question fits the same on every surface. Composers stop here. */
export const ASK_QUESTION_MAX = 4096;
export const askRequestSchema = z.object({
    question: z.string().trim().min(1).max(ASK_QUESTION_MAX),
    threadId: z.uuid().optional(),
});
/** The retrieval tools a step row can show. `show_nooks` and `set_title` are display, not steps. */
export const askToolSchema = z.enum(["search_nooks", "list_nooks", "list_tags", "get_nook"]);
const args = z.record(z.string(), z.unknown());
export const askCitationSchema = z.object({
    ref: z.string(),
    nookId: z.uuid(),
    /** Index into the answer's `content`, where the stripped marker stood. */
    offset: z.number().int().min(0),
});
export const askEventSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("turn"), threadId: z.uuid().optional(), messageId: z.uuid() }),
    z.object({ type: z.literal("reasoning"), stepId: z.string(), text: z.string() }),
    z.object({ type: z.literal("tool_call"), stepId: z.string(), tool: askToolSchema, args }),
    z.object({
        type: z.literal("tool_result"),
        stepId: z.string(),
        total: z.number().int(),
        nookIds: z.array(z.uuid()),
    }),
    z.object({ type: z.literal("text"), text: z.string() }),
    z.object({ type: z.literal("citation"), ...askCitationSchema.shape }),
    z.object({
        type: z.literal("show_nooks"),
        items: z.array(z.object({ nook: nookSummarySchema, why: z.string().nullable() })),
    }),
    z.object({ type: z.literal("title"), title: z.string() }),
    z.object({ type: z.literal("error"), code: z.string(), message: z.string() }),
    z.object({ type: z.literal("done"), latencyMs: z.number().int() }),
]);
/** One row of the transcript's step log, as stored and as replayed on reload. */
export const askStepSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("reasoning"), stepId: z.string(), text: z.string() }),
    z.object({
        type: z.literal("tool"),
        stepId: z.string(),
        tool: askToolSchema,
        args,
        total: z.number().int(),
        nookIds: z.array(z.uuid()),
    }),
]);
export const askMessageSchema = z.discriminatedUnion("role", [
    z.object({
        id: z.uuid(),
        role: z.literal("user"),
        content: z.string(),
        createdAt: z.iso.datetime(),
    }),
    z.object({
        id: z.uuid(),
        role: z.literal("assistant"),
        content: z.string(),
        status: z.enum(["streaming", "complete", "cancelled", "error"]),
        citations: z.array(askCitationSchema),
        steps: z.array(askStepSchema),
        /** `nook: null` is a nook deleted since — "No longer in your library". */
        shownNooks: z.array(z.object({ nook: nookSummarySchema.nullable(), nookId: z.uuid(), why: z.string().nullable() })),
        createdAt: z.iso.datetime(),
    }),
]);
export const askThreadSchema = z.object({
    id: z.uuid(),
    title: z.string().nullable(),
    createdAt: z.iso.datetime(),
    messages: z.array(askMessageSchema),
});
/** `GET /v1/ask/threads` (block 42) — newest `updatedAt` first. */
export const askThreadListSchema = z.object({
    items: z.array(z.object({ id: z.uuid(), title: z.string().nullable(), updatedAt: z.iso.datetime() })),
    nextCursor: z.string().nullable(),
});
/** `PATCH /v1/ask/threads/:id` — the sidebar's Rename. 60 is the generated title's own cap. */
export const askThreadRenameSchema = z.object({ title: z.string().trim().min(1).max(60) });
/** `PATCH /v1/ask/messages/:id/opened` — PRD §11's answer-quality proxy. */
export const askOpenedSchema = z.object({ nookId: z.uuid() });
//# sourceMappingURL=ask.js.map